/*
  Shared slider behaviour for [data-scroll-slider].

  Deliberately dependency-free and cheap:
  - one passive scroll listener per slider, coalesced into a rAF
  - ResizeObserver only to re-test overflow, not to drive layout
  - no slide width maths; widths come from the theme grid, so this works
    with any responsive column setup

  Markup contract (see snippets/slider-controls.liquid):
    [data-scroll-slider]
      [data-scroll-slider-track]         the scroller
      [data-scroll-slider-prev/next]     optional arrow buttons
      [data-scroll-slider-progress-bar]  optional progress fill
*/

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ScrollSlider(root) {
    this.root = root;
    this.track = root.querySelector('[data-scroll-slider-track]');
    if (!this.track) return;

    this.prev = root.querySelector('[data-scroll-slider-prev]');
    this.next = root.querySelector('[data-scroll-slider-next]');
    this.bar = root.querySelector('[data-scroll-slider-progress-bar]');
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prev) this.prev.addEventListener('click', this.step.bind(this, -1));
    if (this.next) this.next.addEventListener('click', this.step.bind(this, 1));

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(this.track);
    } else {
      window.addEventListener('resize', this.update, { passive: true });
    }

    this.update();
  }

  /* Distance to advance: one full visible page, minus a slide's worth of
     overlap so the user keeps a visual anchor. Falls back to the track width. */
  ScrollSlider.prototype.pageSize = function () {
    var slide = this.track.firstElementChild;
    var slideWidth = slide ? slide.getBoundingClientRect().width : 0;
    var visible = this.track.clientWidth;
    if (!slideWidth) return visible;
    // advance by whole slides that fit, at least one
    var perPage = Math.max(1, Math.floor(visible / slideWidth));
    var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return perPage * (slideWidth + gap);
  };

  ScrollSlider.prototype.step = function (direction) {
    this.track.scrollBy({
      left: this.pageSize() * direction,
      behavior: REDUCED.matches ? 'auto' : 'smooth',
    });
  };

  ScrollSlider.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  };

  ScrollSlider.prototype.update = function () {
    this.ticking = false;

    var max = this.track.scrollWidth - this.track.clientWidth;
    // sub-pixel layout rounding leaves a pixel or two of phantom scroll
    var overflowing = max > 2;
    var left = Math.abs(this.track.scrollLeft);

    this.root.setAttribute('data-scroll-slider-overflow', overflowing ? 'true' : 'false');

    if (this.prev) this.prev.disabled = !overflowing || left <= 1;
    if (this.next) this.next.disabled = !overflowing || left >= max - 1;

    if (this.bar) {
      // fill represents the visible portion, positioned by scroll progress
      var ratio = this.track.clientWidth / this.track.scrollWidth;
      var progress = max > 0 ? left / max : 0;
      var width = Math.min(1, ratio) * 100;
      this.bar.style.width = width + '%';
      this.bar.style.transform = 'translateX(' + progress * ((100 / width) * 100 - 100) + '%)';
    }
  };

  ScrollSlider.prototype.destroy = function () {
    this.track.removeEventListener('scroll', this.onScroll);
    if (this.ro) this.ro.disconnect();
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-scroll-slider]').forEach(function (el) {
      if (el.scrollSlider) return;
      el.scrollSlider = new ScrollSlider(el);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init.bind(null, undefined));

  // Theme editor: sections are re-rendered without a page load
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-scroll-slider]').forEach(function (el) {
      if (el.scrollSlider) el.scrollSlider.destroy();
    });
  });

  window.ScrollSlider = ScrollSlider;
})();
